import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, Checkbox, DatePicker, Input, MoneyInput, PageHeader, Select, Switch, Textarea, useToast } from '../../../components/ui';
import { LineItemsEditor, lineError, newLine } from '../../../components/common/LineItemsEditor/LineItemsEditor';
import { JobPicker, VendorPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, TableSection, TotalsBox, TwoColumn } from '../../../components/common/Parts/Parts';
import { PAYMENT_MODE_OPTIONS } from '../../../constants/payments';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { toISODate } from '../../../utils/date';
import { computeTotals } from '../../../lib/pricing';
import { BillUpload } from '../../../components/common/BillUpload/BillUpload';
import { vendorService } from '../../vendors/services/vendorService';
import { jobService } from '../../jobs/services/jobService';
import { purchaseService } from '../services/purchaseService';
import type { PurchaseInput } from '../types/purchaseTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const PURPOSES = [
  { value: 'stock', label: 'For stock', description: 'Goes into the shop stock' },
  { value: 'for_service', label: 'For a job', description: 'Added to stock and issued to the job straight away' },
];

export default function PurchaseForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState([newLine()]);
  const [saving, setSaving] = useState(false);
  const { form, setForm, set, errors, setErrors } = useForm({
    vendorId: params.get('vendorId') || null, vendorName: '', vendorBillNo: '', billDate: toISODate(new Date()), isGst: true,
    purpose: (params.get('serviceId') ? 'for_service' : 'stock') as 'stock' | 'for_service', serviceId: params.get('serviceId') || null, serviceLabel: '',
    billPhotoUrl: '', notes: '', payNow: false, payAmount: null, payMode: 'upi', payRef: '',
  });

  useEffect(() => {
    if (form.vendorId) vendorService.get(form.vendorId).then((v) => setForm((f) => ({ ...f, vendorName: v.name }))).catch(() => {});
    if (form.serviceId) jobService.get(form.serviceId).then((j) => setForm((f) => ({ ...f, serviceLabel: `${j.serviceNo} · ${j.customer?.name}` }))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = computeTotals(items.map((l) => ({ ...l, qty: Number(l.qty) })), form.isGst);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.vendorId) errs.vendorId = 'Pick the supplier';
    if (form.purpose === 'for_service' && !form.serviceId) errs.serviceId = 'Pick the job';
    if (!items.length) errs.lines = 'Add at least one product';
    else errs.lines = lineError(items, { requireItem: true }) ?? undefined;
    if (form.payNow && !(form.payAmount > 0)) errs.payAmount = 'Enter the amount paid';
    if (form.payNow && form.payAmount > totals.grandTotal) errs.payAmount = 'More than the bill total';
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error(errs.lines || 'Please fix the highlighted fields');

    const body: PurchaseInput = {
      vendorId: form.vendorId as string,
      vendorBillNo: trimOrUndef(form.vendorBillNo),
      billDate: form.billDate || undefined,
      billPhotoUrl: form.billPhotoUrl || undefined,
      isGst: form.isGst,
      items: items.map((l) => ({ itemId: l.itemId as string, qty: Number(l.qty), rate: l.rate ?? 0, discount: l.discount || 0, gstRate: Number(l.gstRate) || 0, unit: l.unit })),
      purpose: form.purpose,
      serviceId: form.purpose === 'for_service' ? form.serviceId ?? undefined : undefined,
      notes: trimOrUndef(form.notes),
      ...(form.payNow ? { payment: { amount: form.payAmount, mode: form.payMode, referenceNo: trimOrUndef(form.payRef), date: form.billDate || undefined } } : {}),
    };
    setSaving(true);
    try {
      const data = await purchaseService.create(body);
      toast.success(`Purchase ${data.purchaseNo} saved and stock updated`);
      navigate(`/purchases/${data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
      <Button type="submit" form="purchase-form" leftIcon="check" loading={saving}>Save purchase</Button>
    </>
  );

  return (
    <>
      <PageHeader title="New purchase" subtitle="Enter the supplier's bill. Quantities and rates cannot be changed later (cancel and re-enter instead)." actions={actions} />
      <form id="purchase-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Supplier bill">
                <VendorPicker required value={form.vendorId} selectedLabel={form.vendorName} onChange={(id, v) => setForm((f) => ({ ...f, vendorId: id, vendorName: v?.name ?? '' }))} error={errors.vendorId} containerClassName="sm:col-span-2" footer={
                  <Button variant="ghost" size="sm" leftIcon="plus" fullWidth className="justify-start text-brand-600" onClick={() => navigate('/vendors/new')}>Add a new supplier</Button>
                } />
                <Input label="Supplier bill number" value={form.vendorBillNo} onChange={set('vendorBillNo')} placeholder="As printed on their bill" />
                <DatePicker label="Bill date" value={form.billDate} onChange={set('billDate')} clearable={false} max={toISODate(new Date())} />
                <Select label="Bought for" options={PURPOSES} value={form.purpose} onChange={set('purpose')} searchable={false} />
                {form.purpose === 'for_service' ? (
                  <JobPicker required query={{ status: 'pending,assigned,in_progress,on_hold,completed' }} value={form.serviceId} selectedLabel={form.serviceLabel} onChange={(id, s) => setForm((f) => ({ ...f, serviceId: id, serviceLabel: s ? `${s.serviceNo} · ${s.customer?.name}` : '' }))} error={errors.serviceId} />
                ) : (
                  <div />
                )}
                <BillUpload value={form.billPhotoUrl} onChange={set('billPhotoUrl')} />
              </FormSection>

              <TableSection title="Materials bought" subtitle="Only stock materials. Add new ones in Materials first.">
                <LineItemsEditor lines={items} onChange={setItems} isGst={form.isGst} itemType="material" requireItem textLabel="Material" rateLabel="Cost rate" addLabel="Add material" />
              </TableSection>

              <FormSection title="Payment and notes">
                <div className="sm:col-span-2">
                  <Checkbox label="I paid for this bill now" description="Records the payment together with the purchase" checked={form.payNow} onChange={(v) => setForm((f) => ({ ...f, payNow: v, payAmount: v ? f.payAmount ?? totals.grandTotal : f.payAmount }))} />
                </div>
                {form.payNow && (
                  <>
                    <MoneyInput label="Amount paid" value={form.payAmount} onChange={set('payAmount')} error={errors.payAmount} />
                    <Select label="Mode" options={PAYMENT_MODE_OPTIONS} value={form.payMode} onChange={set('payMode')} searchable={false} />
                    <Input label="UTR / cheque no" value={form.payRef} onChange={set('payRef')} placeholder="Optional" />
                  </>
                )}
                <Textarea label="Notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={2000} containerClassName="sm:col-span-2" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Bill total" />
              <div className="space-y-4 p-5 pt-4">
                <Switch label="GST bill" description="Supplier charged CGST + SGST" checked={form.isGst} onChange={set('isGst')} />
                <TotalsBox totals={totals} isGst={form.isGst} label="Bill total" />
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
